import clsx from "clsx";
import React from "react";

import { Dialog } from "@excalidraw/excalidraw/components/Dialog";
import {
  LockedIcon,
  LoadIcon,
  PlusIcon,
  TrashIcon,
  pencilIcon,
} from "@excalidraw/excalidraw/components/icons";
import { useI18n } from "@excalidraw/excalidraw/i18n";

import { DEFAULT_WORKFILE_ID, type WorkFile } from "../data/workfiles";

import "./WorkFilesDialog.scss";

const formatUpdatedAt = (timestamp: number) => {
  try {
    return new Intl.DateTimeFormat(undefined, {
      dateStyle: "medium",
      timeStyle: "short",
    }).format(timestamp);
  } catch (error) {
    console.error(error);
    return new Date(timestamp).toLocaleString();
  }
};

export const WorkFilesDialog = ({
  activeWorkFileId,
  isRemote,
  username,
  workFiles,
  onCloseRequest,
  onCreate,
  onOpen,
  onRename,
  onDelete,
}: {
  activeWorkFileId: string;
  isRemote: boolean;
  username: string | null;
  workFiles: WorkFile[];
  onCloseRequest: () => void;
  onCreate: (name: string) => void;
  onOpen: (workFileId: string) => void;
  onRename: (workFileId: string, name: string) => void;
  onDelete: (workFileId: string) => void;
}) => {
  const { t } = useI18n();
  const [createDraftName, setCreateDraftName] = React.useState("");
  const [editingWorkFileId, setEditingWorkFileId] = React.useState<
    string | null
  >(null);
  const [deletingWorkFileId, setDeletingWorkFileId] = React.useState<
    string | null
  >(null);
  const [draftName, setDraftName] = React.useState("");
  const renameInputRef = React.useRef<HTMLInputElement>(null);

  const stopRenaming = React.useCallback(() => {
    setEditingWorkFileId(null);
    setDraftName("");
  }, []);

  const stopDeleting = React.useCallback(() => {
    setDeletingWorkFileId(null);
  }, []);

  const startRenaming = React.useCallback((workFile: WorkFile) => {
    setDeletingWorkFileId(null);
    setEditingWorkFileId(workFile.id);
    setDraftName(workFile.name || "");
  }, []);

  const startDeleting = React.useCallback(
    (workFileId: string) => {
      stopRenaming();
      setDeletingWorkFileId(workFileId);
    },
    [stopRenaming],
  );

  const submitRename = React.useCallback(
    (workFileId: string) => {
      onRename(workFileId, draftName);
      stopRenaming();
    },
    [draftName, onRename, stopRenaming],
  );

  const submitCreate = React.useCallback(() => {
    onCreate(createDraftName);
    setCreateDraftName("");
  }, [createDraftName, onCreate]);

  const submitDelete = React.useCallback(
    (workFileId: string) => {
      onDelete(workFileId);
      stopDeleting();
    },
    [onDelete, stopDeleting],
  );

  React.useEffect(() => {
    if (!editingWorkFileId) {
      return;
    }

    renameInputRef.current?.focus();
    renameInputRef.current?.select();
  }, [editingWorkFileId]);

  React.useEffect(() => {
    if (
      (editingWorkFileId || deletingWorkFileId) &&
      !workFiles.some(
        (workFile) =>
          workFile.id === editingWorkFileId ||
          workFile.id === deletingWorkFileId,
      )
    ) {
      stopRenaming();
      stopDeleting();
    }
  }, [
    deletingWorkFileId,
    editingWorkFileId,
    stopDeleting,
    stopRenaming,
    workFiles,
  ]);

  const activeWorkFile =
    workFiles.find((workFile) => workFile.id === activeWorkFileId) || null;
  const otherWorkFiles = workFiles.filter(
    (workFile) => workFile.id !== activeWorkFileId,
  );

  const renderWorkFileCard = (
    workFile: WorkFile,
    opts?: { featured?: boolean },
  ) => {
    const isActive = workFile.id === activeWorkFileId;
    const isDefault = workFile.id === DEFAULT_WORKFILE_ID;
    const isEditing = workFile.id === editingWorkFileId;
    const isDeleting = workFile.id === deletingWorkFileId;

    return (
      <div
        key={workFile.id}
        className={clsx("WorkFilesDialog__item", {
          "WorkFilesDialog__item--featured": opts?.featured,
          "WorkFilesDialog__item--active": isActive,
        })}
      >
        <div className="WorkFilesDialog__itemHeader">
          <div className="WorkFilesDialog__itemContent">
            <div className="WorkFilesDialog__itemNameRow">
              <div className="WorkFilesDialog__itemName">
                {workFile.name || t("labels.untitled")}
              </div>
              <div className="WorkFilesDialog__itemBadges">
                {isDefault && (
                  <span className="WorkFilesDialog__badge WorkFilesDialog__badge--secondary">
                    {t("workFiles.default")}
                  </span>
                )}
                {isDefault && (
                  <span className="WorkFilesDialog__badge WorkFilesDialog__badge--protected">
                    {LockedIcon}
                    <span>{t("workFiles.protected")}</span>
                  </span>
                )}
                {isActive && (
                  <span className="WorkFilesDialog__badge">
                    {t("workFiles.current")}
                  </span>
                )}
              </div>
            </div>
            <div className="WorkFilesDialog__itemMeta">
              {t("workFiles.lastEdited", {
                date: formatUpdatedAt(workFile.updatedAt),
              })}
            </div>
            {isActive && (
              <p className="WorkFilesDialog__itemStatus">
                {isRemote
                  ? t("workFiles.activeHintRemote")
                  : t("workFiles.activeHintLocal")}
              </p>
            )}
          </div>
        </div>

        {isEditing ? (
          <form
            className="WorkFilesDialog__renameForm"
            onSubmit={(event) => {
              event.preventDefault();
              submitRename(workFile.id);
            }}
          >
            <input
              ref={renameInputRef}
              type="text"
              className="WorkFilesDialog__nameInput"
              value={draftName}
              placeholder={t("labels.untitled")}
              aria-label={t("labels.name")}
              onChange={(event) => setDraftName(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === "Escape") {
                  event.preventDefault();
                  stopRenaming();
                }
              }}
            />
            <div className="WorkFilesDialog__itemActions">
              <button
                type="submit"
                className="WorkFilesDialog__secondaryButton WorkFilesDialog__secondaryButton--primary"
              >
                {t("workFiles.saveName")}
              </button>
              <button
                type="button"
                className="WorkFilesDialog__secondaryButton"
                onClick={stopRenaming}
              >
                {t("buttons.cancel")}
              </button>
            </div>
          </form>
        ) : isDeleting ? (
          <div className="WorkFilesDialog__confirmDelete">
            <p className="WorkFilesDialog__confirmDeleteCopy">
              {t(
                isRemote
                  ? "workFiles.deleteConfirmRemote"
                  : "workFiles.deleteConfirm",
              )}
            </p>
            <div className="WorkFilesDialog__itemActions">
              <button
                type="button"
                className="WorkFilesDialog__secondaryButton WorkFilesDialog__secondaryButton--danger"
                onClick={() => submitDelete(workFile.id)}
              >
                {TrashIcon}
                <span>{t("labels.delete")}</span>
              </button>
              <button
                type="button"
                className="WorkFilesDialog__secondaryButton"
                onClick={stopDeleting}
              >
                {t("buttons.cancel")}
              </button>
            </div>
          </div>
        ) : (
          <div className="WorkFilesDialog__itemActions">
            {!isActive && (
              <button
                type="button"
                className="WorkFilesDialog__secondaryButton WorkFilesDialog__secondaryButton--accent"
                onClick={() => onOpen(workFile.id)}
              >
                {LoadIcon}
                <span>{t("workFiles.open")}</span>
              </button>
            )}
            <button
              type="button"
              className="WorkFilesDialog__secondaryButton"
              onClick={() => startRenaming(workFile)}
            >
              {pencilIcon}
              <span>{t("workFiles.rename")}</span>
            </button>
            {!isDefault && (
              <button
                type="button"
                className="WorkFilesDialog__secondaryButton WorkFilesDialog__secondaryButton--danger"
                onClick={() => startDeleting(workFile.id)}
              >
                {TrashIcon}
                <span>{t("labels.delete")}</span>
              </button>
            )}
          </div>
        )}
      </div>
    );
  };

  return (
    <Dialog
      className="WorkFilesDialog__dialog"
      onCloseRequest={onCloseRequest}
      title={t("workFiles.title")}
      size="wide"
    >
      <div className="WorkFilesDialog">
        <div className="WorkFilesDialog__overview">
          <section className="WorkFilesDialog__hero">
            <span className="WorkFilesDialog__eyebrow">
              {t("workFiles.storageLabel")}
            </span>
            <h3 className="WorkFilesDialog__heroTitle">
              {isRemote
                ? t("workFiles.storageModeRemote")
                : t("workFiles.storageModeLocal")}
            </h3>
            <p className="WorkFilesDialog__description">
              {isRemote
                ? t("workFiles.remoteDescription", {
                    username: username || t("auth.menu"),
                  })
                : t("workFiles.description")}
            </p>
            <p className="WorkFilesDialog__privacy">
              {isRemote
                ? t("workFiles.privacyRemote")
                : t("workFiles.privacyLocal")}
            </p>
          </section>

          <div className="WorkFilesDialog__stats">
            <section className="WorkFilesDialog__statCard">
              <span className="WorkFilesDialog__statLabel">
                {t("workFiles.totalFilesLabel")}
              </span>
              <strong className="WorkFilesDialog__statValue">
                {workFiles.length}
              </strong>
            </section>
            <section className="WorkFilesDialog__statCard">
              <span className="WorkFilesDialog__statLabel">
                {t("workFiles.activeFileLabel")}
              </span>
              <strong className="WorkFilesDialog__statValue WorkFilesDialog__statValue--truncate">
                {activeWorkFile?.name || t("labels.untitled")}
              </strong>
            </section>
          </div>
        </div>

        <section className="WorkFilesDialog__createPanel">
          <div className="WorkFilesDialog__createCopy">
            <h3 className="WorkFilesDialog__sectionTitle">
              {t("workFiles.createPanelTitle")}
            </h3>
            <p className="WorkFilesDialog__sectionHint">
              {t("workFiles.createPrompt")}
            </p>
          </div>
          <form
            className="WorkFilesDialog__createForm"
            onSubmit={(event) => {
              event.preventDefault();
              submitCreate();
            }}
          >
            <input
              type="text"
              className="WorkFilesDialog__nameInput"
              value={createDraftName}
              placeholder={t("labels.untitled")}
              aria-label={t("labels.name")}
              onChange={(event) => setCreateDraftName(event.target.value)}
            />
            <button
              type="submit"
              className="WorkFilesDialog__secondaryButton WorkFilesDialog__secondaryButton--primary"
            >
              {PlusIcon}
              <span>{t("workFiles.createAndOpen")}</span>
            </button>
          </form>
        </section>

        {activeWorkFile && (
          <section className="WorkFilesDialog__section">
            <div className="WorkFilesDialog__sectionHeader">
              <div>
                <h3 className="WorkFilesDialog__sectionTitle">
                  {t("workFiles.activeSectionTitle")}
                </h3>
                <p className="WorkFilesDialog__sectionHint">
                  {t("workFiles.activeSectionHint")}
                </p>
              </div>
            </div>
            {renderWorkFileCard(activeWorkFile, { featured: true })}
          </section>
        )}

        <section className="WorkFilesDialog__section">
          <div className="WorkFilesDialog__sectionHeader">
            <div>
              <h3 className="WorkFilesDialog__sectionTitle">
                {t("workFiles.otherSectionTitle")}
              </h3>
              <p className="WorkFilesDialog__sectionHint">
                {t("workFiles.listHint")}
              </p>
            </div>
            <span className="WorkFilesDialog__sectionCount">
              {t("workFiles.totalFiles", {
                count: workFiles.length,
              })}
            </span>
          </div>
          {otherWorkFiles.length ? (
            <div className="WorkFilesDialog__list">
              {otherWorkFiles.map((workFile) => renderWorkFileCard(workFile))}
            </div>
          ) : (
            <div className="WorkFilesDialog__emptyState">
              <p className="WorkFilesDialog__emptyStateTitle">
                {t("workFiles.emptyStateTitle")}
              </p>
              <p className="WorkFilesDialog__emptyStateHint">
                {t("workFiles.emptyStateHint")}
              </p>
            </div>
          )}
        </section>
      </div>
    </Dialog>
  );
};
